/**
 * Traffic intent detection — DC-WA-FLOW-JL26
 *
 * Ads Manager sends high-intent traffic with:
 *   utm_term=[WCA][FS2][HI][180D]
 *   flow=high_intent_jl26
 *
 * The landing reads those parameters once on load, persists them for the
 * session, and uses them to (a) pre-select the advertised procedure in the
 * qualification wizard and (b) label every Meta event with the flow so cost
 * per HI lead can be measured separately.
 *
 * NOTE: This never sends data anywhere by itself. It only reads the URL.
 */

const STORAGE_KEY = 'dc_traffic_intent_jl26'
const HIGH_INTENT_FLOW = 'high_intent_jl26'
const HIGH_INTENT_TERM_MARKER = '[HI]'

export type TrafficIntent = {
  isHighIntent: boolean
  utmTerm: string | null
  flow: string | null
  utmSource: string | null
  utmMedium: string | null
  utmCampaign: string | null
  utmContent: string | null
}

const EMPTY_INTENT: TrafficIntent = {
  isHighIntent: false,
  utmTerm: null,
  flow: null,
  utmSource: null,
  utmMedium: null,
  utmCampaign: null,
  utmContent: null,
}

function readFromUrl(): TrafficIntent | null {
  if (typeof window === 'undefined') return null

  const params = new URLSearchParams(window.location.search)
  const utmTerm = params.get('utm_term')
  const flow = params.get('flow')

  const hasAnyTracking =
    utmTerm ||
    flow ||
    params.get('utm_source') ||
    params.get('utm_medium') ||
    params.get('utm_campaign') ||
    params.get('utm_content')

  if (!hasAnyTracking) return null

  const normalizedTerm = (utmTerm || '').toUpperCase()

  return {
    isHighIntent:
      flow === HIGH_INTENT_FLOW ||
      normalizedTerm.includes(HIGH_INTENT_TERM_MARKER),
    utmTerm,
    flow,
    utmSource: params.get('utm_source'),
    utmMedium: params.get('utm_medium'),
    utmCampaign: params.get('utm_campaign'),
    utmContent: params.get('utm_content'),
  }
}

function readFromStorage(): TrafficIntent | null {
  if (typeof window === 'undefined') return null
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    return JSON.parse(raw) as TrafficIntent
  } catch {
    return null
  }
}

function persist(intent: TrafficIntent) {
  if (typeof window === 'undefined') return
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(intent))
  } catch {
    // Storage unavailable (private mode) — intent still works for this page view
  }
}

export function getTrafficIntent(): TrafficIntent {
  const fromUrl = readFromUrl()
  if (fromUrl) {
    persist(fromUrl)
    return fromUrl
  }
  return readFromStorage() || EMPTY_INTENT
}

/** Compact params appended to every Meta event for HI-vs-cold attribution. */
export function getIntentEventParams(): Record<string, string> {
  const intent = getTrafficIntent()
  const params: Record<string, string> = {
    traffic_intent: intent.isHighIntent ? 'high_intent' : 'standard',
  }
  if (intent.utmTerm) params.utm_term = intent.utmTerm
  if (intent.flow) params.flow = intent.flow
  if (intent.utmCampaign) params.utm_campaign = intent.utmCampaign
  return params
}
